import {
  normalizeAdminHoverToggleDelayMsOverride,
  type AdminHoverToggleDelayMsOverride,
} from "./adminHoverToggleDelayOverride";
import {
  normalizeAdminGameSpeedMultiplier,
  type AdminGameSpeedMultiplier,
} from "./adminGameSpeedPolicy";

export interface AdminRuntimePreferences {
  readonly adminGameSpeedMultiplier: AdminGameSpeedMultiplier;
  readonly adminHoverToggleDelayMsOverride: AdminHoverToggleDelayMsOverride;
  readonly showLockedHovSkillContent: boolean;
}

export interface AdminRuntimePreferenceStorage {
  load(): AdminRuntimePreferences;
  save(preferences: AdminRuntimePreferences): void;
}

interface LocalStorageAdminRuntimePreferenceStorageOptions {
  readonly ownerId: string;
  readonly storage?: Storage | null;
}

const ADMIN_RUNTIME_PREFERENCES_VERSION = 3;
const ADMIN_RUNTIME_PREFERENCES_KEY_PREFIX =
  "hover-idle:admin-runtime-preferences";

const DEFAULT_ADMIN_RUNTIME_PREFERENCES: AdminRuntimePreferences = {
  adminGameSpeedMultiplier: normalizeAdminGameSpeedMultiplier(1),
  adminHoverToggleDelayMsOverride: null,
  showLockedHovSkillContent: false,
};

export function createLocalStorageAdminRuntimePreferenceStorage(
  options: LocalStorageAdminRuntimePreferenceStorageOptions,
): AdminRuntimePreferenceStorage {
  const storage = options.storage ?? resolveLocalStorage();
  const key = `${ADMIN_RUNTIME_PREFERENCES_KEY_PREFIX}:${options.ownerId}`;

  return {
    load(): AdminRuntimePreferences {
      if (storage === null) {
        return DEFAULT_ADMIN_RUNTIME_PREFERENCES;
      }
      try {
        const raw = storage.getItem(key);
        if (raw === null) {
          return DEFAULT_ADMIN_RUNTIME_PREFERENCES;
        }
        return decodeAdminRuntimePreferences(JSON.parse(raw) as unknown);
      } catch {
        return DEFAULT_ADMIN_RUNTIME_PREFERENCES;
      }
    },

    save(preferences: AdminRuntimePreferences): void {
      if (storage === null) {
        return;
      }
      const normalized: AdminRuntimePreferences = {
        adminGameSpeedMultiplier: normalizeAdminGameSpeedMultiplier(
          preferences.adminGameSpeedMultiplier,
        ),
        adminHoverToggleDelayMsOverride:
          normalizeAdminHoverToggleDelayMsOverride(
            preferences.adminHoverToggleDelayMsOverride,
          ),
        showLockedHovSkillContent:
          preferences.showLockedHovSkillContent === true,
      };
      try {
        storage.setItem(
          key,
          JSON.stringify({
            version: ADMIN_RUNTIME_PREFERENCES_VERSION,
            ...normalized,
          }),
        );
      } catch {
        // Runtime preferences are a development aid; storage failure should not
        // block startup or gameplay.
      }
    },
  };
}

function decodeAdminRuntimePreferences(
  value: unknown,
): AdminRuntimePreferences {
  if (!isRecord(value)) {
    return DEFAULT_ADMIN_RUNTIME_PREFERENCES;
  }
  const storedAdminGameSpeed =
    value.adminGameSpeedMultiplier ?? value.adminTimeScale;
  return {
    adminGameSpeedMultiplier:
      normalizeAdminGameSpeedMultiplier(storedAdminGameSpeed),
    adminHoverToggleDelayMsOverride: normalizeAdminHoverToggleDelayMsOverride(
      value.adminHoverToggleDelayMsOverride,
    ),
    showLockedHovSkillContent: value.showLockedHovSkillContent === true,
  };
}

function resolveLocalStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
